import Vue from 'vue'
import managementService from '../services/managementService'
import { filter, clone, findIndex } from 'lodash'

const state = {
  members: null,
  membersSearchCount: 0,
  membersCount: 0,
  membersLoading: false,
  membersStatus: {},
  requests: null,
  requestsSearchCount: 0,
  requestsCount: 0,
  requestsLoading: false,
  requestsStatus: {}
}

const mutations = {
  saveMembers (state, data) {
    if (data.replace) {
      state.members = data.items
    } else {
      state.members.push(...data.items)
    }
    state.membersSearchCount = data.searchCount
    state.membersCount = data.totalCount
  },
  clearMembers (state) {
    state.members = null
    state.membersSearchCount = 0
  },
  saveRequests (state, data) {
    if (data.replace) {
      state.requests = data.items
    } else {
      state.requests.push(...data.items)
    }
    state.requestsSearchCount = data.searchCount
    state.requestsCount = data.totalCount
  },
  clearRequests (state) {
    state.requests = null
    state.requestsSearchCount = 0
  },
  setRequestStatus (state, { userId, status }) {
    Vue.set(state.requestsStatus, userId, status)
  },
  setMemberStatus (state, { userId, status }) {
    Vue.set(state.membersStatus, userId, status)
  },
  deleteRequest (state, userId) {
    delete state.requestsStatus[userId]
    const index = findIndex(state.requests, o => o.id === userId)
    state.requests.splice(index, 1)
    state.requestsSearchCount -= 1
    state.requestsCount -= 1
  }
}

const getters = {
  getAdmins (state) {
    return filter(state.members, o => o.isManager)
  },
  getNotAdmins (state) {
    return filter(state.members, o => !o.isManager)
  }
}

const actions = {
  getMembers ({ commit, dispatch }, data) {
    if (state.membersClub !== data.clubCode) {
      commit('clearMembers')
    }
    state.membersLoading = true
    state.membersClub = data.clubCode
    managementService.getMembers(data)
      .then(response => {
        commit('saveMembers', {
          items: response.data.items,
          searchCount: response.data.searchCount,
          totalCount: response.data.totalCount,
          replace: data.offset === 0
        })
        state.membersLoading = false
      })
      .catch((error) => {
        if (error.message !== 'canceled') {
          state.membersLoading = false
          dispatch('alerts/addSomethingWrongAlert', null, {
            root: true
          })
        }
      })
  },
  getRequests ({ commit, dispatch }, data) {
    if (state.requestsClub !== data.clubCode) {
      commit('clearRequests')
    }
    state.requestsLoading = true
    state.requestsClub = data.clubCode
    managementService.getRequests(data)
      .then(response => {
        commit('saveRequests', {
          items: response.data.items,
          searchCount: response.data.searchCount,
          totalCount: response.data.totalCount,
          replace: data.offset === 0
        })
        commit('club/saveRequestsCount',
          response.data.totalCount,
          { root: true }
        )
        state.requestsLoading = false
      })
      .catch((error) => {
        if (error.message !== 'canceled') {
          state.requestsLoading = false
          dispatch('alerts/addSomethingWrongAlert', null, {
            root: true
          })
        }
      })
  },
  acceptRequest ({ state, commit, dispatch }, { userId, clubCode }) {
    const status = clone(state.requestsStatus[userId]) || {}
    status.accepting = true
    commit('setRequestStatus', {
      userId: userId,
      status: status
    })
    setTimeout(() => {
      managementService.acceptRequest({ userId, clubCode })
        .then((response) => {
          if (!response.data.errorMessage) {
            commit('club/saveRequestsCount',
              state.requestsCount - 1,
              { root: true }
            )
            commit('deleteRequest', userId)
            commit('club/triggerInfoUpdate', null, { root: true })
            commit('club/triggerMembersUpdate', null, { root: true })
            return
          }
          dispatch('alerts/addSomethingWrongAlert', response.data.errorMessage, {
            root: true
          })
          status.accepting = false
          commit('setRequestStatus', {
            userId: userId,
            status: status
          })
        })
        .catch(() => {
          dispatch('alerts/addSomethingWrongAlert', null, {
            root: true
          })
          status.accepting = false
          commit('setRequestStatus', {
            userId: userId,
            status: status
          })
        })
    }, 200)
  },
  rejectRequest ({ state, commit, dispatch }, { userId, clubCode }) {
    const status = clone(state.requestsStatus[userId]) || {}
    status.rejecting = true
    commit('setRequestStatus', {
      userId: userId,
      status: status
    })
    setTimeout(() => {
      managementService.rejectRequest({ userId, clubCode })
        .then((response) => {
          if (response.data.isSuccess) {
            commit('club/saveRequestsCount',
              state.requestsCount - 1,
              { root: true }
            )
            commit('deleteRequest', userId)
            commit('club/triggerInfoUpdate', null, { root: true })
            commit('club/triggerMembersUpdate', null, { root: true })
            return
          }
          dispatch('alerts/addSomethingWrongAlert', response.data.errorMessage, {
            root: true
          })
          status.rejecting = false
          commit('setRequestStatus', {
            userId: userId,
            status: status
          })
        })
        .catch(() => {
          dispatch('alerts/addSomethingWrongAlert', null, {
            root: true
          })
          status.rejecting = false
          commit('setRequestStatus', {
            userId: userId,
            status: status
          })
        })
    }, 200)
  },
  excludeMember ({ state, commit, dispatch }, { userId, clubCode, clubId, isCurrentUser }) {
    const status = clone(state.membersStatus[userId]) || {}
    if (status.excluding) {
      return
    }
    status.excluding = true
    commit('setMemberStatus', {
      userId: userId,
      status: status
    })
    setTimeout(() => {
      managementService.rejectRequest({ userId, clubCode, commit })
        .then((response) => {
          if (response.data.isSuccess) {
            delete state.membersStatus[userId]
            const index = findIndex(state.members, o => o.id === userId)
            state.members.splice(index, 1)
            state.membersSearchCount -= 1
            state.membersCount -= 1
            commit('club/triggerInfoUpdate', null, { root: true })
            commit('club/triggerMembersUpdate', null, { root: true })
            return
          }
          dispatch('alerts/addSomethingWrongAlert', response.data.errorMessage, {
            root: true
          })
          status.excluding = false
          commit('setMemberStatus', {
            userId: userId,
            status: status
          })
        })
        .catch(() => {
          dispatch('alerts/addSomethingWrongAlert', null, {
            root: true
          })
          status.excluding = false
          commit('setMemberStatus', {
            userId: userId,
            status: status
          })
        })
    }, 200)
  }
}

export const clubManagement = {
  namespaced: true,
  state,
  mutations,
  getters,
  actions
}
